<template>
  <div class="w-full">
    <form
      class="space-y-4"
      @submit="onSubmit"
    >
      <div class="flex flex-col">
        <label
          for="comment-content"
          class="mb-2 text-sm font-medium text-gray-700"
        >
          Agregar comentario
        </label>
        <Field
          v-slot="{ field, meta }"
          name="content"
          label="Comentario"
          :rules="{ required: true, min: 5 }"
        >
          <textarea
            v-bind="field"
            id="comment-content"
            rows="3"
            class="w-full resize-none rounded-md border px-3 py-2 text-sm shadow-sm transition-colors duration-200 focus:border-lk-green focus:outline-none"
            :class="{
              'border-red-500': !meta.valid && meta.touched,
              'border-gray-300': meta.valid || !meta.touched,
            }"
            placeholder="Escribe tu comentario aquí..."
            :disabled="isSubmitting"
          />
        </Field>
        <ErrorMessage
          name="content"
          class="mt-1 text-sm text-red-500"
        />
      </div>
      <div class="flex justify-end space-x-3">
        <button
          type="button"
          :disabled="isSubmitting"
          class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors duration-200 hover:bg-gray-50 focus:outline-none focus:ring-gray-500"
          @click="$emit('cancel')"
        >
          Cancelar
        </button>
        <button
          type="submit"
          :disabled="isSubmitting"
          class="flex items-center rounded-md bg-lk-green px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-green-600 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span v-if="!isSubmitting">Enviar comentario</span>
          <span
            v-else
            class="flex items-center"
          >
            <svg
              class="-ml-1 mr-2 size-4 animate-spin text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              />
            </svg>
            Enviando...
          </span>
        </button>
      </div>
    </form>
    <div
      v-if="showConfirmation"
      class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
      @click.self="(showConfirmation = false)"
    >
      <div class="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div class="mb-4 flex items-center">
          <div class="flex size-10 items-center justify-center rounded-full bg-amber-100">
            <ExclamationTriangleIcon class="size-6 text-amber-600" />
          </div>
          <h3 class="ml-3 text-lg font-medium text-gray-900">Confirmar envío</h3>
        </div>

        <p class="mb-6 text-sm text-gray-600">
          Al enviar este comentario, se notificará a la otra parte involucrada en la incidencia. ¿Estás seguro de que
          deseas continuar?
        </p>

        <div class="flex space-x-3">
          <button
            type="button"
            class="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors duration-200 hover:bg-gray-50 focus:outline-none focus:ring-gray-500"
            @click="(showConfirmation = false)"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="flex-1 rounded-md bg-lk-green px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-green-600 focus:outline-none"
            @click="confirmSubmit"
          >
            Confirmar envío
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useForm, Field, ErrorMessage } from 'vee-validate';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import { useMutation } from 'vue-query';
import incidenceReportsApi from 'api/incidence-reports';

interface IncidenceReportCommentFormProps {
  incidenceReportId: number;
}

const props = defineProps<IncidenceReportCommentFormProps>();

const emit = defineEmits<{
  success: [comment: IncidenceReportComment];
  cancel: [];
}>();

const { handleSubmit, resetForm } = useForm();
const showConfirmation = ref(false);
const formValues = ref<{ content?: string }>({});

const createCommentMutation = useMutation(
  () =>
    incidenceReportsApi.createComment(props.incidenceReportId, {
      content: formValues.value.content!.trim(),
    }),
  {
    onSuccess: (response) => {
      emit('success', response.data.incidenceReportComment);
      resetForm();
      formValues.value = {};
      showConfirmation.value = false;
    },
    onError: () => {
      showConfirmation.value = false;
    },
  },
);

const isSubmitting = computed(() => createCommentMutation.isLoading.value);

const onSubmit = handleSubmit((values) => {
  if (!values.content?.trim()) return;
  formValues.value = values;
  showConfirmation.value = true;
});

function confirmSubmit() {
  if (!formValues.value.content?.trim()) return;
  createCommentMutation.mutate();
}
</script>
